import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiChevronDown } from 'react-icons/fi';
import { apiGetCategories } from '../../api';
import CategoryDrawer from './CategoryDrawer';
import LanguageMenu from './LanguageMenu';
import { isLanguageCategory, isLanguageBrowse } from '../../utils/languageNavigation';
import { useToast } from '../../context/ToastContext';

const CategoryNav = () => {
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { addToast } = useToast();
  const categoryListRef = useRef(null);
  const activeCategoryRef = useRef(null);

  useEffect(() => {
    apiGetCategories()
      .then((data) => setCategories(data.categories || []))
      .catch((error) => addToast(error.message || 'Failed to load categories', 'error'));
  }, [addToast]);

  const openMenu = useCallback(() => setOpen(true), []);
  const closeMenu = useCallback(() => setOpen(false), []);

  // Derive the currently active category from the URL (?category=Name)
  const activeCategory = location.pathname.startsWith('/shop')
    ? new URLSearchParams(location.search).get('category')
    : null;
  const languageBrowse =
    location.pathname === '/shop' &&
    isLanguageBrowse(new URLSearchParams(location.search));
  const activeSort = location.pathname.startsWith('/shop')
    ? new URLSearchParams(location.search).get('sort')
    : location.pathname === '/'
      ? location.hash.slice(1)
      : null;

  useEffect(() => {
    const list = categoryListRef.current;
    const activeItem = activeCategoryRef.current;
    if (!list || !activeItem) return;

    const centeredPosition =
      activeItem.offsetLeft - list.clientWidth / 2 + activeItem.clientWidth / 2;
    list.scrollTo({ left: Math.max(0, centeredPosition), behavior: 'smooth' });
  }, [activeCategory, categories]);

  return (
    <nav
      className="relative border-b border-black/20"
      style={{ backgroundColor: '#2D3142' }}
      aria-label="Book categories"
    >
      <div className="w-full mx-auto flex items-center gap-6 px-4 py-2.5">
        {/* All Categories trigger - opens the side drawer */}
        <span
          onClick={openMenu}
          aria-expanded={open}
          aria-haspopup="true"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' ? openMenu() : null)}
          className="flex items-center gap-2 text-sm font-semibold whitespace-nowrap text-white hover:text-amber-400 cursor-pointer select-none shrink-0"
        >
          <FiMenu className="text-base" />
          <span>All Categories</span>
          <FiChevronDown className="text-sm" />
        </span>

        {/* Inline category links - each navigates directly to that category */}
        <ul
          ref={categoryListRef}
          className="flex items-center gap-6 overflow-x-auto list-none flex-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {categories.map((category) => {
            const isActive =
              activeCategory === category.name ||
              (isLanguageCategory(category.name) && languageBrowse);
            return (
              <li
                key={category.id}
                ref={isActive ? activeCategoryRef : null}
                className="shrink-0"
              >
                {isLanguageCategory(category.name) ? (
                  <LanguageMenu label={category.name} dropdown />
                ) : (
                  <Link
                    to={`/shop?category=${encodeURIComponent(category.name).replace(/%20/g, '+')}`}
                    aria-current={isActive ? 'page' : undefined}
                    className={`text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'text-amber-400 underline underline-offset-4'
                        : 'text-white hover:text-amber-400'
                    }`}
                  >
                    {category.name}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <CategoryDrawer
        open={open}
        onClose={closeMenu}
        categories={categories}
        highlightedCategory={activeCategory}
        highlightedSort={activeSort}
      />
    </nav>
  );
};

export default CategoryNav;
